import './Quotes.css';
import { BarChart, Bar, XAxis, YAxis, PieChart, Pie, Tooltip, Legend, Cell, Customized } from 'recharts';
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import { generateQuote } from "./QuoteGenerator";
import moment from "moment";
import { getInterestRateForProjections, depositModeNameForPlan } from "../Utils/ConfigUtil";
import {
    beneficiaryCalendarYearsAge,
    beneficiaryCalendarYearsAgeAtMaturity,
    roundHalfUp,
} from "../Utils/PlanUtils";
import { formatCurrency, formatPercentage, formatUnits } from "../Internationalization/Formatters";
import {
    getNetSavingsForQuote,
    getTotalGrantForQuote,
    getTotalGrantInterestForQuote,
    pieChartMinimumPercentForLabel
} from "../Utils/ProjectionUtils";
import { nth } from "../Utils/PlanUtils";

import { sprintf } from "../Utils/FormattingUtils";

const COLORS = ['RGB(131.325,204,255)', 'RGB(191.25,224.4,204)', 'RGB(114.75,216.75,63.75)', 'RGB(255,206.55,130.05)'];


//If setQuoteImage is set, a PNG image of the rendered component will be sent to the function
// after the component has finished mounting.  Intended to generate PDF from the component.
// If fullScale is set, the component will be displayed at full scale, otherwise it will be scaled down to fit on a page
const SelfInitiatedQuoteSummary = ({ state, setQuoteImage, page, totalPages, fullScale }) => {
    const componentRef = useRef(); // Reference to the component
    const { t, i18n } = useTranslation();
    const [quoteAttributes, setQuoteAttributes] = useState(null);
    const [pieChartData, setPieChartData] = useState([]);
    const [totalPie, setTotalPie] = useState(0);

    //Once we have all of the data we need for the quote, we assume it is displayed and
    // generate the image for the quote
    useEffect(() => {
        if (!setQuoteImage || !componentRef.current) { // || pieChartData.length === 0) {
            return;
        }

        html2canvas(componentRef.current).then((canvas) => {
            setQuoteImage(canvas.toDataURL('image/png'));
        });

    }, [setQuoteImage, quoteAttributes, componentRef.current]);

    useEffect(() => {
        setQuoteAttributes(generateQuote(state));
    }, [state]);

    useEffect(() => {
        if (!quoteAttributes) {
            return;
        }

        let data = [];

        const grant = getTotalGrantForQuote(quoteAttributes); //roundHalfUp(getTotalGrantForQuote(quoteAttributes), 0);
        const grantIncome = getTotalGrantInterestForQuote(quoteAttributes); //roundHalfUp(getTotalGrantInterestForQuote(quoteAttributes), 0);
        const EAPs = quoteAttributes.EAPAmount;
        const netSavings = getNetSavingsForQuote(quoteAttributes);
        if (grant !== 0) {
            data.push({ name: t('Grant'), value: grant });
        }
        if (grantIncome !== 0) {
            data.push({ name: t('Grant Income'), value: grantIncome });
        }
        if (EAPs !== 0) {
            data.push({ name: t('EAPs'), value: EAPs });
        }
        if (netSavings !== 0) {
            data.push({ name: t('Net Savings'), value: netSavings });
        }


        setPieChartData(data);

        setTotalPie(data.reduce((acc, cur) => acc + cur.value, 0));

    }, [quoteAttributes]);

    //Displays a currency value in the format of the current locale
    const displayCurrency = (value, decimals = 0) => {
        return formatCurrency(roundHalfUp(value, decimals), i18n.language, decimals);
    }

    //Displays a date in the format MMM D, YYYY
    // If the date is invalid, an empty string is returned
    const displayDate = (value) => {
        const ret = moment(value).format('MMM D, YYYY');
        if (ret === 'Invalid date') { return ''; }
        return ret;
    }


    //Draw labels for the pie graph inside the pie pieces
    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name, value }) => {
        const RADIAN = Math.PI / 180;
        const radius = innerRadius + (outerRadius - innerRadius) * (1 - percent) * 0.8;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        //if (percent > pieChartMinimumPercentForLabel) {
        return (
            <svg>
                <text x={x} y={y + 10} fill="#111" textAnchor={x > cx ? 'middle' : 'middle'} dominantBaseline="central">
                    {displayCurrency(value)}
                </text>

                {/* <text x={x} y={y - 10} fill="#111" textAnchor={x > cx ? 'middle' : 'middle'} dominantBaseline="central">
                        {name}
                    </text>*/}
            </svg>
        )
        /*} else {
            //If the piece of the pie is too small to display the label, we write it at the bottom left
            //This only works if one piece is this small...
            return (
                <svg>
                    <text x={0} y={cy * 2 - 20} fill="#111" textAnchor={'left'} dominantBaseline="central" fontSize={13}>
                        {displayCurrency(value)}
                    </text>

                    <text x={0} y={cy * 2 - 40} fill="#111" textAnchor={'left'} dominantBaseline="central" fontSize={13}>
                        {name}
                    </text>
                </svg>
            )
        }*/

    };

    const pageCountLabel = () => {
        if (!page || !totalPages) {
            return '';
        }
        return sprintf(t('x of y'), page, totalPages);
    }


    if (!quoteAttributes || !pieChartData) {
        return (
            <div>Generating quote...</div>
        )
    } else {
        return (
            <div ref={componentRef} className={`quote ${fullScale ? 'full-scale' : ''}`}>
                <div className="quote--header">
                    <div className="quote--header__left">
                        <h1>{t('Children\'s Education Funds Inc.')}</h1>
                        <h4>{t('Plan Illustration Estimate -')} {t('Self-Initiated Option Plan')}</h4>
                    </div>
                    <div className={"quote--header__right"}>
                        <h1>{t(depositModeNameForPlan(state))}</h1>
                        <h2>{pageCountLabel()}</h2>
                    </div>
                </div>

                <div className={'visualizations'}>
                    <div className={'visualizations__left'}>
                        <p style={{ textAlign: 'center', marginTop: '0px' }}>
                            {t('Projected Estimated Amount available in your Self-Initiated Option Plan when your child is ready to attend post-secondary: ')}
                            <br></br><span className="bold-currency-small">{displayCurrency(totalPie, 0)}*</span>
                        </p>
                        <div className={'pie-chart-container'}>
                            <PieChart width={400} height={400}>
                                <Pie
                                    dataKey="value"
                                    isAnimationActive={false}
                                    data={pieChartData}
                                    cx={180}
                                    cy={180}
                                    outerRadius={150}
                                    fill="#8884d8"
                                    labelLine={false}
                                    label={renderCustomizedLabel}
                                    stroke={false}
                                >
                                    {pieChartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={(value, name, props) => {
                                    return [displayCurrency(value), name];
                                }} />
                                <Legend
                                    layout="horizontal"
                                    verticalAlign="bottom"
                                    align="center"
                                    wrapperStyle={{ marginTop: '20px' }}
                                    content={({ payload }) => (
                                        <ul style={{ display: "flex", flexDirection: "row", listStyle: "none", margin: 0, padding: 0 }}>
                                            {payload.map((entry, index) => (
                                                <li key={`legend-item-${index}`} style={{ display: "flex", alignItems: "center", marginRight: "10px" }}>
                                                    <span
                                                        style={{
                                                            display: "inline-block",
                                                            width: "20px",
                                                            height: "20px",
                                                            backgroundColor: entry.color,
                                                            marginRight: "5px",
                                                        }}
                                                    />
                                                    <span style={{ color: "black", fontSize: "12px", fontWeight: 'bold' }}>{entry.value}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                />
                                {/* Add this text element for the center number */}
                                {/*<text 
                                    x={180} 
                                    y={180} 
                                    textAnchor="middle" 
                                    dominantBaseline="middle" 
                                    style={{ fontSize: '24px', fontWeight: 'bold', fill: '#000' }}
                                >
                                    {displayCurrency(totalPie, 0)}
                                </text>*/}
                            </PieChart>
                            <div className={'pie-chart-overlay'}></div>
                        </div>
                        {/* <div className='pie-chart-center-label'>
                            {displayCurrency(totalPie, 0)}
                        </div>  */}
                    </div>
                    <div className={'visualizations__right self-initiated'}>
                        <div className={'info'}>
                            {t('Please read page 2 for the assumptions and important information used in preparing this illustration.')}
                        </div>
                        <table>
                            <tbody>
                                <tr>
                                    <td>
                                        {/* <div className={'airmiles-plus'}>{t('PLUS')}</div> */}
                                        {
                                            //TODO: Translation for the below
                                        }
                                        {/* <div>
                                            {sprintf(t("Your contributions will earn you %i AIR MILES Reward Miles over the life of the plan!"), Math.trunc(quoteAttributes.airMilesEarned))}
                                        </div> */}
                                    </td>
                                    <td>
                                        {/* <img width={100} height={100} src={'/images/new_amlogo-2-2.png'}
                                            alt={t('AIR Miles logo')} /> */}
                                    </td>
                                </tr>
                                {state.plan.clbOnly &&
                                    <tr>
                                        <td className={'clb-only-indicator'}>CLB Only</td>
                                    </tr>
                                }
                            </tbody>
                        </table>
                        <div
                            className={'highest-paying-notice'}>{t('Proudly Canadian. Over 30 years of helping you save for a brighter tomorrow.')}{/*<sup>3</sup>*/}
                        </div>
                    </div>
                </div>


                <div className={'details-section'}>
                    <table>
                        <tbody>
                            <tr>
                                <td>
                                    <span className={'label'}>{t('Contribution Amount')}</span>
                                    <span className={'value'}>{displayCurrency(quoteAttributes.amountPerDeposit, 2)}</span>
                                </td>
                                <td>
                                    <span className={'label'}>{t('Number of Units')}</span>
                                    <span className={'value'}>{formatUnits(state.plan.numberOfUnits)}</span>
                                </td>

                                <td>
                                    <span className={'label'}>{t('Depository Fees')}<sup>1</sup></span>
                                    <span className={'value'}>{displayCurrency(quoteAttributes.depositoryFee)}</span>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <span className={'label'}>{t('Initial Contribution Amount')}</span>
                                    <span
                                        className={'value'}>{displayCurrency(quoteAttributes.initialDepositAmount, 2)}</span>
                                </td>
                                <td>
                                    <span className={'label'}>{t('Total Contributions')}</span>
                                    <span className={'value'}>{displayCurrency(quoteAttributes.totalDeposits)}</span>
                                </td>
                                <td>
                                    <span className={'label'}>{t('Insurance Premiums')}<sup>2</sup></span>
                                    <span className={'value'}>{displayCurrency(quoteAttributes.totalInsuranceAmount)}</span>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <span className={'label'}>{t('Projected Estimated Amount*')}</span>
                                    <span className={'value'}>{displayCurrency(totalPie, 0)}</span>

                                </td>
                                <td>
                                    <span className={'label'}>{t('Sales Charge')}</span>
                                    <span className={'value'}>{displayCurrency(quoteAttributes.enrolmentFee)}</span>
                                </td>
                                <td>
                                    <span className={'label'}>{t('Total Net Contributions')}</span>
                                    <span className={'value'}>{displayCurrency(getNetSavingsForQuote(quoteAttributes))}</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className={'maturity-summary'}>
                    {sprintf(t("Your plan has been set up for the beneficiary to commence post-secondary study in %i, during the year of the beneficiary's %ith birthday."), moment(state.plan.maturityDate).year(), beneficiaryCalendarYearsAgeAtMaturity(state))}
                </div>

                <div className={'details-section'}>

                    <table>
                        <tbody>
                            <tr>
                                <td>
                                    <span className={'label'}>{t('Beneficiary Name')}</span>
                                    <span
                                        className={'value'}>{state.beneficiary.firstName} {state.beneficiary.lastName}</span>
                                </td>
                                <td>
                                    <span className={'label'}>{t('Effective Date')}</span>
                                    <span className="value">
                                        {new Intl.DateTimeFormat(i18n.resolvedLanguage || i18n.language || 'en', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric'
                                        }).format(new Date(state.plan.effectiveDate + 'T00:00:00'))}
                                    </span>
                                </td>
                                <td>
                                    <span className={'label'}>{t('Completion Insurance')}</span>
                                    <span className={'value'}>{state.plan.completionInsurance ? t("YES") : t("NO")}</span>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <span className={'label'}>{t('Beneficiary Birth Date')}</span>
                                    <span className="value">
                                        {new Intl.DateTimeFormat(i18n.resolvedLanguage || i18n.language || 'en', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric'
                                        }).format(new Date(state.beneficiary.birthdate + 'T00:00:00'))}
                                    </span>
                                </td>
                                <td>
                                    <span className={'label'}>{t('Beneficiary Age on Dec. 31 of Maturity Year')}</span>
                                    <span
                                        className={'value'}>{moment(state.plan.maturityDate).year() - moment(state.beneficiary.birthdate).year()}</span>
                                </td>
                                <td>
                                    <span className={'label'}>{t('Beneficiary Insurance')}</span>
                                    <span className={'value'}>{state.plan.nomineeInsurance ? t("YES") : t("NO")}</span>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <span className={'label'}>{t('Mode')}</span>
                                    <span className={'value'}>{state.plan.depositMode}-{state.plan.depositCode}</span>
                                </td>
                                <td>
                                    <span className={'label'}>{t('Maturity Date')}</span>
                                    <span className="value">
                                        {new Intl.DateTimeFormat(i18n.resolvedLanguage || i18n.language || 'en', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric'
                                        }).format(new Date(state.plan.maturityDate + 'T00:00:00'))}
                                    </span>
                                </td>
                                <td>
                                    <span className={'label'}>{t('Critical Illness Insurance')}</span>
                                    <span className={'value'}>{state.plan.criticalIllnessInsurance ? t("YES") : t("NO")}</span>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <span className={'label'}>{t('Frequency')}</span>
                                    <span className={'value'}>{t(depositModeNameForPlan(state))}</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className={'footer'}>
                </div>


                <div>
                    {
                        //JSON.stringify(generateQuote(state, true).projections, null, 2)
                    }
                </div>
            </div>
        )
    }
}

export default SelfInitiatedQuoteSummary;